import { useMemo, useState } from "react";
import {
  BUDGET_CATEGORIES, SPENDING_DATA, NET_WORTH_DATA,
  INVESTMENTS, ACCOUNTS, fmt,
} from "../../data/mockData";
import { useIsMobile } from "../../hooks/useIsMobile";

// ─── Grade system ─────────────────────────────────────────────────────────────
const GRADES = {
  excellent:    { label: "Excellent",       color: "#52D68A", bg: "rgba(82,214,138,0.09)",   border: "rgba(82,214,138,0.28)"   },
  good:         { label: "On Track",        color: "#D4AF37", bg: "rgba(212,175,55,0.09)",   border: "rgba(212,175,55,0.28)"   },
  "needs-work": { label: "Needs Work",      color: "#E8C87A", bg: "rgba(232,200,122,0.09)",  border: "rgba(232,200,122,0.28)"  },
  critical:     { label: "Needs Attention", color: "#F87171", bg: "rgba(248,113,113,0.09)",  border: "rgba(248,113,113,0.28)"  },
};

// ─── Motivational message pools ───────────────────────────────────────────────
function computeInsights() {

  // ── 1. Net Worth ──────────────────────────────────────────────────────────
  const nwFirst       = NET_WORTH_DATA[0].value;
  const nwLast        = NET_WORTH_DATA[NET_WORTH_DATA.length - 1].value;
  const nwPrev        = NET_WORTH_DATA[NET_WORTH_DATA.length - 2].value;
  const nwGrowthPct   = ((nwLast - nwFirst) / nwFirst) * 100;
  const nwMonthChange = nwLast - nwPrev;

  let nwGrade, nwHeadline, nwDetail;
  if (nwGrowthPct >= 7) {
    nwGrade    = "excellent";
    nwHeadline = `Up ${nwGrowthPct.toFixed(1)}% in 6 months`;
    nwDetail   = `Your net worth has grown by ${nwGrowthPct.toFixed(1)}% over the last six months — that is top-tier wealth building. You added ${fmt(nwMonthChange)} in the last month alone. Stay disciplined and you will reach major financial milestones ahead of schedule.`;
  } else if (nwGrowthPct >= 3) {
    nwGrade    = "good";
    nwHeadline = `Up ${nwGrowthPct.toFixed(1)}% in 6 months`;
    nwDetail   = `Solid and steady — a ${nwGrowthPct.toFixed(1)}% net worth increase in six months reflects real discipline. Compound growth rewards consistency. Keep contributing monthly and the curve will steepen.`;
  } else if (nwGrowthPct >= 0) {
    nwGrade    = "needs-work";
    nwHeadline = `Up ${nwGrowthPct.toFixed(1)}% in 6 months`;
    nwDetail   = `Your net worth is trending up but slowly at ${nwGrowthPct.toFixed(1)}%. Boosting your savings rate by even 5% or redirecting discretionary spending to investments would meaningfully accelerate this number.`;
  } else {
    nwGrade    = "critical";
    nwHeadline = `Down ${Math.abs(nwGrowthPct).toFixed(1)}% in 6 months`;
    nwDetail   = `Your net worth has declined ${Math.abs(nwGrowthPct).toFixed(1)}% recently. Identify your largest recurring expenses and create a plan to reverse the trend. Even small cuts compound into big improvements over time.`;
  }

  // ── 2. Budget ─────────────────────────────────────────────────────────────
  const overCats   = BUDGET_CATEGORIES.filter(c => c.spent > c.allocated);
  const totalBudg  = BUDGET_CATEGORIES.reduce((s, c) => s + c.allocated, 0);
  const totalSpent = BUDGET_CATEGORIES.reduce((s, c) => s + c.spent, 0);
  const budgetUsed = (totalSpent / totalBudg) * 100;
  const leftover   = totalBudg - totalSpent;

  let budgetGrade, budgetHeadline, budgetDetail;
  if (overCats.length === 0 && budgetUsed < 85) {
    budgetGrade    = "excellent";
    budgetHeadline = `${(100 - budgetUsed).toFixed(0)}% budget headroom`;
    budgetDetail   = `Every single category is under budget this month — a rare achievement. You used only ${budgetUsed.toFixed(0)}% of your total allocation, leaving ${fmt(leftover)} unspent. That extra cash is now working for you in savings and investments.`;
  } else if (overCats.length === 0) {
    budgetGrade    = "good";
    budgetHeadline = `${budgetUsed.toFixed(0)}% of budget used`;
    budgetDetail   = `All categories are within their limits — you are in control of your spending. A little tight at ${budgetUsed.toFixed(0)}% used, but ${fmt(leftover)} is still available. Finish the month strong.`;
  } else if (overCats.length === 1) {
    const pctOver = ((overCats[0].spent / overCats[0].allocated - 1) * 100).toFixed(0);
    budgetGrade    = "needs-work";
    budgetHeadline = `${overCats[0].name} over by ${pctOver}%`;
    budgetDetail   = `Five out of six categories are on track — great discipline overall. ${overCats[0].name} ran ${pctOver}% over budget this month. A simple weekly check on that category is all it takes to bring it back into line.`;
  } else {
    budgetGrade    = "critical";
    budgetHeadline = `${overCats.length} categories over budget`;
    budgetDetail   = `${overCats.map(c => c.name).join(", ")} all exceeded their budgets this month. Consider setting stricter weekly spending caps for these categories and reviewing whether the allocated amounts still reflect your priorities.`;
  }

  // ── 3. Savings Rate ───────────────────────────────────────────────────────
  const recent      = SPENDING_DATA[SPENDING_DATA.length - 1];
  const savingsRate = ((recent.income - recent.spending) / recent.income) * 100;
  const savedAmt    = recent.income - recent.spending;

  let savingsGrade, savingsHeadline, savingsDetail;
  if (savingsRate >= 40) {
    savingsGrade    = "excellent";
    savingsHeadline = `${savingsRate.toFixed(0)}% savings rate`;
    savingsDetail   = `Saving ${savingsRate.toFixed(0)}% of your income places you firmly in the elite tier of personal finance. At this rate you are putting away ${fmt(savedAmt * 12)} per year. Keep this up and financial independence is genuinely within reach years ahead of the average timeline.`;
  } else if (savingsRate >= 20) {
    savingsGrade    = "good";
    savingsHeadline = `${savingsRate.toFixed(0)}% savings rate`;
    savingsDetail   = `A ${savingsRate.toFixed(0)}% savings rate clears the recommended 20% benchmark — well done. You saved ${fmt(savedAmt)} this month. Every month at this level pushes your financial goals meaningfully forward.`;
  } else if (savingsRate >= 10) {
    savingsGrade    = "needs-work";
    savingsHeadline = `${savingsRate.toFixed(0)}% savings rate`;
    savingsDetail   = `You are saving ${savingsRate.toFixed(0)}% of your income — a start, but below the recommended 20% target. Try automating a transfer of an extra ${fmt(recent.income * 0.05)} on payday. You likely will not miss it, and it closes half the gap immediately.`;
  } else {
    savingsGrade    = "critical";
    savingsHeadline = `${savingsRate.toFixed(0)}% savings rate`;
    savingsDetail   = `A ${savingsRate.toFixed(0)}% savings rate leaves very little buffer for emergencies or long-term goals. List your top three discretionary expenses this week — reducing those alone could unlock the financial breathing room you need.`;
  }

  // ── 4. Investments ────────────────────────────────────────────────────────
  const avgGain   = INVESTMENTS.reduce((s, i) => s + i.gain, 0) / INVESTMENTS.length;
  const totalGain = INVESTMENTS.reduce((s, i) => s + i.gainAmt, 0);
  const winners   = INVESTMENTS.filter(i => i.gain > 0).length;

  let investGrade, investHeadline, investDetail;
  if (avgGain >= 20) {
    investGrade    = "excellent";
    investHeadline = `+${avgGain.toFixed(1)}% avg portfolio gain`;
    investDetail   = `Your portfolio is delivering a ${avgGain.toFixed(1)}% average return — outstanding performance that beats most benchmarks. You have ${fmt(totalGain)} in unrealized gains across ${winners} winning positions. Your diversification strategy across index funds and equities is clearly working. Stay the course.`;
  } else if (avgGain >= 10) {
    investGrade    = "good";
    investHeadline = `+${avgGain.toFixed(1)}% avg portfolio gain`;
    investDetail   = `A ${avgGain.toFixed(1)}% average portfolio return is healthy and above most savings rates. You are up ${fmt(totalGain)} in unrealized gains. Long-term investors who stay invested through volatility consistently outperform those who try to time the market.`;
  } else if (avgGain >= 5) {
    investGrade    = "needs-work";
    investHeadline = `+${avgGain.toFixed(1)}% avg portfolio gain`;
    investDetail   = `Your investments are growing at ${avgGain.toFixed(1)}% on average. Consider increasing monthly contributions to your best-performing index funds — even ${fmt(100)} more per month compounded over 20 years makes a substantial difference to your final balance.`;
  } else {
    investGrade    = "critical";
    investHeadline = `${avgGain.toFixed(1)}% avg portfolio gain`;
    investDetail   = `Portfolio performance below 5% deserves a closer look. Review your asset allocation and consider whether a shift toward diversified low-cost index funds aligns better with your long-term goals. Ensure you are fully utilizing tax-advantaged accounts like your IRA before taxable investments.`;
  }

  // ── 5. Debt ───────────────────────────────────────────────────────────────
  const debtAccts  = ACCOUNTS.filter(a => a.balance < 0);
  const totalDebt  = Math.abs(debtAccts.reduce((s, a) => s + a.balance, 0));
  const payingDown = debtAccts.filter(a => a.change > 0);
  const debtReducd = payingDown.reduce((s, a) => s + a.change, 0);

  let debtGrade, debtHeadline, debtDetail;
  if (debtAccts.length === 0) {
    debtGrade    = "excellent";
    debtHeadline = `Debt-free`;
    debtDetail   = `Being debt-free is the ultimate financial freedom — every dollar you earn is yours. Maintain this position by staying clear of high-interest consumer debt, and channel what would have been debt payments directly into investments.`;
  } else if (payingDown.length === debtAccts.length) {
    debtGrade    = "good";
    debtHeadline = `Paying down ${fmt(debtReducd)}/mo`;
    debtDetail   = `You are actively reducing all ${debtAccts.length} debt account${debtAccts.length > 1 ? "s" : ""} — excellent financial discipline. Paying down ${fmt(debtReducd)} this month means you are saving real money on future interest. Consistency here will deliver outsized returns once debt is cleared.`;
  } else if (payingDown.length > 0) {
    debtGrade    = "needs-work";
    debtHeadline = `${fmt(totalDebt)} total debt`;
    debtDetail   = `Some debts are decreasing but others are not. Focus extra payments on your highest-interest balance first — the avalanche method. Every dollar of high-interest debt you eliminate is like earning that interest rate guaranteed, risk-free.`;
  } else {
    debtGrade    = "critical";
    debtHeadline = `${fmt(totalDebt)} total debt`;
    debtDetail   = `Your debt balances are not decreasing. Even an extra ${fmt(50)}–${fmt(100)} payment per month on your highest-interest account will make a meaningful difference over time. Consider automating a small extra payment so it happens without relying on willpower.`;
  }

  // ── Overall health score ──────────────────────────────────────────────────
  const gradeScore = { excellent: 4, good: 3, "needs-work": 2, critical: 1 };
  const avg = [nwGrade, budgetGrade, savingsGrade, investGrade, debtGrade]
    .map(g => gradeScore[g])
    .reduce((a, b) => a + b, 0) / 5;

  const overallGrade = avg >= 3.5 ? "excellent" : avg >= 2.5 ? "good" : avg >= 1.5 ? "needs-work" : "critical";
  const overallMsg = {
    excellent:    "You are performing at an elite level across the board. Keep the habits going and let compounding do the heavy lifting.",
    good:         "Your finances are in good shape overall. A few targeted improvements will unlock the next level of financial performance.",
    "needs-work": "You have clear opportunities to strengthen your position. Small, consistent actions in 2–3 areas will make a measurable difference.",
    critical:     "Several areas need focused attention. Prioritize the highest-impact changes first — you do not need to fix everything at once.",
  }[overallGrade];

  return {
    overall: { grade: overallGrade, message: overallMsg },
    areas: [
      { id: "networth",    icon: "◈", label: "Net Worth",   grade: nwGrade,      headline: nwHeadline,      detail: nwDetail      },
      { id: "budget",      icon: "◎", label: "Budget",      grade: budgetGrade,  headline: budgetHeadline,  detail: budgetDetail  },
      { id: "savings",     icon: "◉", label: "Savings",     grade: savingsGrade, headline: savingsHeadline, detail: savingsDetail },
      { id: "investments", icon: "△", label: "Investments", grade: investGrade,  headline: investHeadline,  detail: investDetail  },
      { id: "debt",        icon: "⊞", label: "Debt",        grade: debtGrade,    headline: debtHeadline,    detail: debtDetail    },
    ],
  };
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function FinancialCoach() {
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(null);
  const { overall, areas } = useMemo(computeInsights, []);
  const overallCfg = GRADES[overall.grade];

  const toggle = (id) => setExpanded(prev => prev === id ? null : id);

  return (
    <div style={{ marginBottom: "24px" }}>
      <div style={{
        background: "var(--bg-elevated)",
        border: `1px solid ${overallCfg.border}`,
        borderRadius: "20px",
        overflow: "hidden",
      }}>

        {/* ── Summary header ────────────────────────────────────────────── */}
        <div style={{
          padding: isMobile ? "18px 20px 16px" : "22px 28px 18px",
          borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "flex-start",
          justifyContent: "space-between", gap: "16px",
        }}>
          <div style={{ flex: 1 }}>
            {/* Label row */}
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px", flexWrap: "wrap" }}>
              <span style={{
                fontSize: "10px", fontFamily: "'JetBrains Mono', monospace",
                color: "var(--text-muted)", letterSpacing: "0.14em", textTransform: "uppercase",
              }}>
                Financial Coach
              </span>
              <span style={{
                fontSize: "9px", fontFamily: "'JetBrains Mono', monospace",
                padding: "2px 9px", borderRadius: "8px",
                background: overallCfg.bg, color: overallCfg.color,
                border: `1px solid ${overallCfg.border}`,
                fontWeight: "700", letterSpacing: "0.06em",
              }}>
                {overallCfg.label.toUpperCase()}
              </span>
            </div>
            {/* Coach message */}
            <div style={{
              fontSize: isMobile ? "13px" : "14px",
              color: "var(--text-primary)",
              fontFamily: "'Capsule Sans', sans-serif",
              fontWeight: "500", lineHeight: 1.55,
              maxWidth: "680px",
            }}>
              {overall.message}
            </div>
          </div>

          {/* Health dot indicators (desktop only) */}
          {!isMobile && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px", flexShrink: 0, paddingTop: "2px" }}>
              <div style={{ display: "flex", gap: "5px" }}>
                {areas.map(a => {
                  const cfg = GRADES[a.grade];
                  return (
                    <div
                      key={a.id}
                      title={`${a.label}: ${cfg.label}`}
                      style={{
                        width: "9px", height: "9px", borderRadius: "50%",
                        background: cfg.color, opacity: 0.8,
                        cursor: "pointer", transition: "transform 0.15s",
                      }}
                      onClick={() => toggle(a.id)}
                    />
                  );
                })}
              </div>
              <span style={{ fontSize: "9px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>
                tap area for details
              </span>
            </div>
          )}
        </div>

        {/* ── Area cards ────────────────────────────────────────────────── */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(5, 1fr)",
        }}>
          {areas.map((area, idx) => {
            const cfg    = GRADES[area.grade];
            const isExp  = expanded === area.id;
            const isLast = idx === areas.length - 1;

            // Border logic
            const rightBorder  = isMobile
              ? (idx % 2 === 0 && !isLast ? "1px solid var(--border)" : "none")
              : (!isLast ? "1px solid var(--border)" : "none");
            const bottomBorder = isMobile
              ? (idx < areas.length - 1 ? "1px solid var(--border)" : "none")
              : "none";
            // Last card on mobile spans 2 columns when odd total
            const gridColumn = (isMobile && isLast && areas.length % 2 !== 0) ? "span 2" : "auto";

            return (
              <div
                key={area.id}
                onClick={() => toggle(area.id)}
                style={{
                  padding: isMobile ? "16px 14px 20px" : "20px",
                  borderRight:  rightBorder,
                  borderBottom: bottomBorder,
                  gridColumn,
                  cursor: "pointer",
                  transition: "background 0.15s",
                  background: isExp ? cfg.bg : "transparent",
                  position: "relative",
                }}
                onMouseEnter={e => { if (!isExp) e.currentTarget.style.background = "var(--bg-hover)"; }}
                onMouseLeave={e => { if (!isExp) e.currentTarget.style.background = "transparent"; }}
              >
                {/* Area icon + label row */}
                <div style={{
                  display: "flex", alignItems: "center",
                  justifyContent: "space-between", marginBottom: "10px",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontSize: "14px", color: cfg.color, lineHeight: 1 }}>{area.icon}</span>
                    <span style={{
                      fontSize: "9px", fontFamily: "'JetBrains Mono', monospace",
                      color: "var(--text-muted)", textTransform: "uppercase",
                      letterSpacing: "0.1em",
                    }}>
                      {area.label}
                    </span>
                  </div>
                  {/* Grade badge */}
                  <span style={{
                    fontSize: "8px", fontFamily: "'JetBrains Mono', monospace",
                    padding: "2px 6px", borderRadius: "6px",
                    background: cfg.bg, color: cfg.color,
                    border: `1px solid ${cfg.border}`,
                    fontWeight: "700", letterSpacing: "0.04em", flexShrink: 0,
                  }}>
                    {cfg.label.toUpperCase()}
                  </span>
                </div>

                {/* Headline metric */}
                <div style={{
                  fontSize: isMobile ? "13px" : "14px",
                  fontFamily: "'Capsule Sans', sans-serif",
                  fontWeight: "700", color: cfg.color,
                  marginBottom: "4px", lineHeight: 1.2,
                }}>
                  {area.headline}
                </div>

                {/* Expand/collapse detail */}
                {isExp ? (
                  <div style={{
                    fontSize: "12px", color: "var(--text-secondary)",
                    lineHeight: 1.65, marginTop: "10px",
                    fontFamily: "'Capsule Sans', sans-serif",
                    borderTop: "1px solid var(--border)", paddingTop: "10px",
                  }}>
                    {area.detail}
                  </div>
                ) : (
                  <div style={{
                    fontSize: "10px", color: "var(--text-faint)",
                    fontFamily: "'JetBrains Mono', monospace",
                    marginTop: "4px",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.5,
                  }}>
                    {area.detail}
                  </div>
                )}

                {/* Tap hint */}
                <div style={{
                  position: "absolute", bottom: "6px", right: "10px",
                  fontSize: "8px", color: "var(--text-faint)",
                  fontFamily: "'JetBrains Mono', monospace",
                }}>
                  {isExp ? "▲ less" : "▼ more"}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}
